import React, {
  useCallback,
  useEffect,
  useRef,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import { useForm } from "@tanstack/react-form";
import { showError } from "../../../../../utils/toast.ts";
import clsx from "clsx";
import { Select } from "../../../../ui/select/select.tsx";
import { TextField } from "../../../../ui/text-field/text-field.tsx";
import { MINUTE_OPTIONS, STATUS_OPTIONS } from "../../options.ts";
import type { SelectOption } from "../../../../ui/select/select.tsx";
import { TextArea } from "../../../../ui/text-area/text-area.tsx";
import {
  entryTotalMinutes,
  getAllocationForProject,
  type ProjectAllocationRow,
} from "../../utils/timesheet-allocation.ts";
import { digitsOnlyInput, normalizePastedMarkdownText } from "../../../../../utils/helpers.ts";
import styles from "./timesheet-entry-form.module.css";
import type { WorkStreamOption } from "../../hooks/use-timesheet-options.ts";
import type { PhaseOption } from "../../hooks/use-timesheet-options.ts";

export type TimesheetEntryFormValues = {
  project: string;
  workStreamId: string;
  phase: string;
  category: string;
  description: string;
  hours: string;
  minutes: string;
  status: string;
};

export type TimesheetEntryFormProps = {
  formId: string;
  initial: TimesheetEntryFormValues;
  projects: SelectOption[];
  categories: SelectOption[];
  phases?: PhaseOption[];
  projectWorkstreamsMap?: Record<string, WorkStreamOption[]>;
  optionsLoading?: boolean;
  categoriesLoading?: boolean;
  onProjectChange?: (projectId: string) => void | Promise<void>;
  onWorkstreamChange?: (projectId: string, workstreamId: string) => void | Promise<void>;
  onSave: (values: TimesheetEntryFormValues) => void;
  action?: ReactNode;
  validate?: boolean;
  resetFieldsOnSubmit?: (keyof TimesheetEntryFormValues)[];
  disabled?: boolean;
  showAsterisks?: boolean;
  /** From timesheet status API — used for hours validation and live allocation toasts. */
  projectAllocations?: ProjectAllocationRow[];
  /** Minutes already logged for a project in other rows (same day, local state). */
  getOtherEntriesMinutesForProject?: (projectId: number) => number;
  /** Fires when form dirty state changes (e.g. edit modal disables Save until true). */
  onDirtyChange?: (dirty: boolean) => void;
  /** Makes only Category mandatory when the rest of the form uses relaxed edit validation. */
  requiredCategory?: boolean;
  /** Fires whenever Category changes so a parent action can reflect its validity. */
  onCategoryChange?: (category: string) => void;
  /** When true, project cannot be changed (e.g. rejected-but-editable entries). */
  disableProjectSelect?: boolean;
};

export function TimesheetEntryForm({
  formId,
  initial,
  projects,
  categories,
  phases = [],
  projectWorkstreamsMap = {},
  optionsLoading = false,
  categoriesLoading = false,
  onProjectChange,
  onWorkstreamChange,
  onSave,
  action,
  validate = true,
  resetFieldsOnSubmit,
  disabled = false,
  showAsterisks = false,
  projectAllocations = [],
  getOtherEntriesMinutesForProject,
  onDirtyChange,
  requiredCategory = false,
  onCategoryChange,
  disableProjectSelect = false,
}: TimesheetEntryFormProps) {
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  const form = useForm<TimesheetEntryFormValues>({
    defaultValues: initial,
    onSubmit: async ({ value }) => {
      onSave(value);
      if (resetFieldsOnSubmit?.length) {
        form.reset();
        if (textareaRef.current) {
          textareaRef.current.style.height = "auto";
        }
      }
    },
  });

  const autoResize = useCallback(
    (e: React.FormEvent<HTMLTextAreaElement>) => {
      const el = e.currentTarget;
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight}px`;
    },
    []
  );

  useEffect(() => {
    if (!initial.project) return;
    if (initial.workStreamId) void onWorkstreamChange?.(initial.project, initial.workStreamId);
    else void onProjectChange?.(initial.project);
  }, [initial.project, initial.workStreamId, onProjectChange, onWorkstreamChange]);

  return (
    <form
      id={formId}
      className={clsx(styles.form, disabled && styles.disabled)}
      onSubmit={(e) => {
        e.preventDefault();
        void form.handleSubmit();
      }}
    >
      <form.Field
        name="project"
        validators={
          validate
            ? { onSubmit: ({ value }) => (!value ? "Required" : undefined) }
            : undefined
        }
      >
        {(field) => (
          <div className={styles.field}>
            <Select
              label={showAsterisks ? "Project *" : "Project"}
              id={`${formId}-project`}
              placeholder={optionsLoading ? "Loading..." : "Select Project"}
              value={field.state.value}
              onChange={(value) => {
                field.handleChange(value);
                form.setFieldValue("category", "");
                onCategoryChange?.("");
                form.setFieldValue("workStreamId", "");
                form.setFieldValue("phase", "");
                void onProjectChange?.(value);
              }}
              options={projects}
              disabled={disabled || optionsLoading || disableProjectSelect}
              searchable
            />
            {field.state.meta.isTouched && field.state.meta.errors[0] && (
              <span className={styles.error}>{field.state.meta.errors[0]}</span>
            )}
          </div>
        )}
      </form.Field>

      <form.Subscribe selector={(state) => state.values.project}>
        {(selectedProject) => {
          const workstreams =
            selectedProject && selectedProject !== "MISCELLANEOUS"
              ? projectWorkstreamsMap[selectedProject] ?? []
              : [];
          if (workstreams.length === 0) return null;

          const workStreamOptions: SelectOption[] = workstreams.map((ws) => ({
            label: ws.workStream,
            value: String(ws.workStreamId),
          }));

          return (
            <form.Field
              name="workStreamId"
              validators={
                validate
                  ? {
                      onSubmit: ({ value }) =>
                        !value ? "Required" : undefined,
                    }
                  : undefined
              }
            >
              {(wsField) => (
                <div className={styles.field}>
                  <Select
                    label={showAsterisks ? "Work Stream *" : "Work Stream"}
                    id={`${formId}-workStream`}
                    placeholder="Select Work Stream"
                    value={wsField.state.value}
                    onChange={(value) => { wsField.handleChange(value); form.setFieldValue("phase", ""); void onWorkstreamChange?.(selectedProject, value); }}
                    options={workStreamOptions}
                    disabled={disabled || optionsLoading}
                  />
                  {wsField.state.meta.isTouched &&
                    wsField.state.meta.errors[0] && (
                      <span className={styles.error}>
                        {wsField.state.meta.errors[0]}
                      </span>
                    )}
                </div>
              )}
            </form.Field>
          );
        }}
      </form.Subscribe>

      {phases.length > 0 && <form.Field name="phase" validators={validate ? { onSubmit: ({ value }) => (!value ? "Required" : undefined) } : undefined}>
        {(field) => <div className={styles.field}><Select label={showAsterisks ? "Phase *" : "Phase"} id={`${formId}-phase`} placeholder="Select Phase" value={field.state.value} onChange={field.handleChange} options={phases.map((phase) => ({ value: phase.value, label: phase.label }))} disabled={disabled || categoriesLoading} />{field.state.meta.isTouched && field.state.meta.errors[0] && <span className={styles.error}>{field.state.meta.errors[0]}</span>}</div>}
      </form.Field>}

      <form.Field
        name="category"
        validators={
          validate || requiredCategory
            ? { onSubmit: ({ value }) => (!value ? "Category is required" : undefined) }
            : undefined
        }
      >
        {(field) => (
          <div className={styles.field}>
            <Select
              label={showAsterisks ? "Category *" : "Category"}
              id={`${formId}-category`}
              placeholder={categoriesLoading ? "Loading categories..." : "Select Category"}
              value={field.state.value}
              onChange={(value) => {
                field.handleChange(value);
                onCategoryChange?.(value);
              }}
              options={categories}
              disabled={disabled || optionsLoading || categoriesLoading}
            />
            {!categoriesLoading && field.state.meta.isTouched && field.state.meta.errors[0] ? (
              <span className={styles.error}>{field.state.meta.errors[0]}</span>
            ) : null}
          </div>
        )}
      </form.Field>

      <form.Field
        name="description"
        validators={
          validate
            ? { onSubmit: ({ value }) => (!value.trim() ? "Required" : undefined) }
            : undefined
        }
      >
        {(field) => (
          <div className={styles.fieldLarge}>
            <TextArea
              ref={textareaRef}
              id={`${formId}-description`}
              label={showAsterisks ? "Task Description *" : "Task Description"}
              placeholder="What did you work on?"
              rows={1}
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onPaste={(event) => {
                const pasted = event.clipboardData.getData("text/plain");
                const cleaned = normalizePastedMarkdownText(pasted);
                if (cleaned === pasted) return;

                event.preventDefault();
                const textarea = event.currentTarget;
                const start = textarea.selectionStart;
                const end = textarea.selectionEnd;
                field.handleChange(`${field.state.value.slice(0, start)}${cleaned}${field.state.value.slice(end)}`);
                requestAnimationFrame(() => {
                  const cursor = start + cleaned.length;
                  textarea.setSelectionRange(cursor, cursor);
                  textarea.style.height = "auto";
                  textarea.style.height = `${textarea.scrollHeight}px`;
                });
              }}
              onInput={autoResize}
              disabled={disabled}
            />
            {field.state.meta.isTouched && field.state.meta.errors[0] && (
              <span className={styles.error}>{field.state.meta.errors[0]}</span>
            )}
          </div>
        )}
      </form.Field>

      <form.Field
        name="hours"
        validators={{
          onSubmit: ({ value, fieldApi }) => {
            const minutes = fieldApi.form.getFieldValue("minutes");
            const project = fieldApi.form.getFieldValue("project");

            const totalMinutes =
              (Number(value || 0) * 60) + Number(minutes || 0);

            const selectedProject = projectAllocations.find(
              (p) => String(p.projectId) === String(project),
            );

            if (selectedProject && totalMinutes > selectedProject.remainingBal) {
              return "Time exceeds remaining allocation";
            }

            // existing checks
            const isZeroHours = value === "0" || value === "00" || (value && Number(value) === 0);
            const isZeroMinutes = !minutes || minutes === "00";

            if (isZeroHours && isZeroMinutes) return "Invalid";
            if (!value && isZeroMinutes) return "Required";

            if (!validate) return undefined;
            if (value && Number(value) < 0) return "Invalid";

            return undefined;
          }
        }}
      >
        {(field) => (
          <div className={styles.fieldSmall}>
            <TextField
              label="Hrs"
              id={`${formId}-hours`}
              type="text"
              inputMode="numeric"
              autoComplete="off"
              value={field.state.value}
              onChange={(e) => {
                const val = digitsOnlyInput(e.target.value, 2);
                field.handleChange(val);
                form.validateField("minutes", "submit");
              }}
              onBlur={field.handleBlur}
              disabled={disabled}
            />
            {field.state.meta.isTouched && field.state.meta.errors[0] && (
              <span className={styles.error}>{field.state.meta.errors[0]}</span>
            )}
          </div>
        )}
      </form.Field>

      <form.Field name="minutes">
        {(field) => (
          <div className={styles.fieldSmall}>
            <Select
              label="Mins"
              id={`${formId}-minutes`}
              placeholder="00"
              value={field.state.value}
              onChange={(v) => {
                field.handleChange(v);
                form.validateField("hours", "submit");
              }}
              options={MINUTE_OPTIONS}
              disabled={disabled}
            />
          </div>
        )}
      </form.Field>

      <form.Field
        name="status"
        validators={
          validate
            ? { onSubmit: ({ value }) => (!value ? "Required" : undefined) }
            : undefined
        }
      >
        {(field) => (
          <div className={styles.field}>
            <Select
              label={showAsterisks ? "Status *" : "Status"}
              id={`${formId}-status`}
              placeholder="Select Status"
              value={field.state.value}
              onChange={field.handleChange}
              options={STATUS_OPTIONS}
              disabled={disabled}
            />
            {field.state.meta.isTouched && field.state.meta.errors[0] && (
              <span className={styles.error}>{field.state.meta.errors[0]}</span>
            )}
          </div>
        )}
      </form.Field>

      {getOtherEntriesMinutesForProject != null &&
        projectAllocations.length > 0 && (
          <TimesheetAllocationToastWatcher
            form={form}
            projectAllocations={projectAllocations}
            getOtherEntriesMinutesForProject={getOtherEntriesMinutesForProject}
          />
        )}

      {onDirtyChange ? (
        <form.Subscribe selector={(state) => state.isDirty}>
          {(isDirty) => (
            <FormDirtySyncEffect
              isDirty={isDirty}
              onDirtyChange={onDirtyChange}
            />
          )}
        </form.Subscribe>
      ) : null}

      {action}
    </form>
  );
}

function TimesheetAllocationToastWatcher({
  form,
  projectAllocations,
  getOtherEntriesMinutesForProject,
}: {
  form: {
    store: {
      subscribe: (cb: () => void) => { unsubscribe: () => void };
      state: { values: TimesheetEntryFormValues };
    };
  };
  projectAllocations: ProjectAllocationRow[];
  getOtherEntriesMinutesForProject: (projectId: number) => number;
}) {
  const values = useSyncExternalStore(
    (onChange) => {
      const subscription = form.store.subscribe(onChange);
      return () => subscription.unsubscribe();
    },
    () => form.store.state.values,
    () => form.store.state.values,
  );
  const { project, hours, minutes } = values;

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (!project || project === "MISCELLANEOUS") return;
      const pid = Number(project);
      if (Number.isNaN(pid)) return;
      const alloc = getAllocationForProject(projectAllocations, pid);
      if (!alloc) return;
      const entryMins = entryTotalMinutes(hours, minutes);
      const other = getOtherEntriesMinutesForProject(pid);
      if (other + entryMins > alloc.remainingBal) {
        showError("Time exceeds remaining allocation for this project.", {
          id: "timesheet-project-allocation",
        });
      }
    }, 450);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [
    project,
    hours,
    minutes,
    projectAllocations,
    getOtherEntriesMinutesForProject,
  ]);

  return null;
}

function FormDirtySyncEffect({
  isDirty,
  onDirtyChange,
}: {
  isDirty: boolean;
  onDirtyChange: (dirty: boolean) => void;
}) {
  useEffect(() => {
    onDirtyChange(isDirty);
  }, [isDirty, onDirtyChange]);
  return null;
}
